import { RegPlate } from '@components';
import { useCustomerVehicles } from '@hooks';
import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

interface IProps {
  customerId: string;
  status?: 'error' | 'warning';
  stickyHeader?: boolean;
  onClear?: () => void;
  onSelect?: (regNumber: string) => void;
  value?: string | null;
  allowClear?: boolean;
}

// Vehicle picker for the unified customer view. Unlike UserVehicleSelect (keyed on a userId,
// app users only), this is fed by every vehicle linked to the customer via their orders/invoices,
// so it works for app and non-app customers alike. Clearing the value means "All vehicles".
export const CustomerVehicleSelect: React.FC<IProps> = observer(
  ({ customerId, status, stickyHeader, onSelect, onClear, value, allowClear = true }) => {
    const { vehicles, loading } = useCustomerVehicles(customerId);

    const options = useMemo(
      () =>
        vehicles.map((v) => {
          const makeModel = [v.make, v.model].filter(Boolean).join(' ');
          return {
            label: (
              <span
                style={{
                  display: 'grid',
                  gridTemplateColumns: '88px 1fr',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <RegPlate size="small">{v.registrationNumber}</RegPlate>
                </span>
                {makeModel && (
                  <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>{makeModel}</span>
                )}
              </span>
            ),
            value: v.registrationNumber,
          };
        }),
      [vehicles]
    );

    return (
      <Select
        allowClear={allowClear}
        loading={loading}
        style={{ width: '100%' }}
        status={status}
        options={options}
        value={value}
        onClear={onClear}
        onSelect={onSelect}
        placeholder="Select a vehicle"
        notFoundContent={loading ? 'Loading…' : 'No vehicles found'}
        // Let the dropdown size to its content so long make/model labels aren't truncated.
        popupMatchSelectWidth={false}
        getPopupContainer={(stickyHeader && ((trigger: any) => trigger.parentElement)) || undefined}
      />
    );
  }
);
